/**
 * InstallApp.js
 * Instalación de MiCoach como aplicación (PWA).
 *
 * Dos experiencias distintas porque los navegadores no son iguales:
 *
 *  · Android / escritorio (Chrome, Edge, Samsung): el navegador nos avisa con
 *    'beforeinstallprompt'. Ahí se puede instalar con un solo botón, así que
 *    se enseña un botón "Instalar app" y punto.
 *  · iPhone / iPad: Safari no permite lanzar la instalación desde la web. Lo
 *    único que se puede hacer es explicar el camino manual, y por eso ahí el
 *    botón abre un mini tutorial con los tres pasos.
 *
 * Se exportan dos piezas:
 *  · <InstallBanner />   aviso discreto, aparece solo y se puede posponer.
 *  · <InstallAppCard />  tarjeta fija para Ajustes, siempre disponible.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Smartphone, Share, SquarePlus, X, Check, Download } from 'lucide-react';
import {
  getInstallMode,
  promptInstall,
  subscribeInstall,
  isIOSSafari,
  isIOSDevice,
  dismissInstallHint,
  installHintSnoozed,
  getLastPortal,
} from '../lib/pwa';
import { hasDecided } from '../lib/consent';

// Modo de instalación actual, recalculado cuando el navegador nos ofrece (o
// nos retira) el diálogo nativo.
function useInstallMode() {
  const [mode, setMode] = useState(() => getInstallMode());

  useEffect(() => subscribeInstall(() => setMode(getInstallMode())), []);

  // Si se instala mientras la pestaña sigue abierta, display-mode cambia y el
  // aviso debe desaparecer sin recargar.
  useEffect(() => {
    const mq = window.matchMedia?.('(display-mode: standalone)');
    if (!mq?.addEventListener) return undefined;
    const onChange = () => setMode(getInstallMode());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return mode;
}

// ─── Tutorial de iOS ─────────────────────────────────────────────────────────
function IOSGuide({ onClose }) {
  // Desde iOS 16.4 Chrome y Firefox también pueden añadir a la pantalla de
  // inicio, pero la opción está escondida en otro sitio según el navegador y
  // no siempre aparece. Por eso el recorrido se explica igual y solo se sugiere
  // Safari como plan B, en vez de afirmar que ahí no se puede.
  const inSafari = isIOSSafari();

  const steps = [
    {
      icon: Share,
      title: 'Pulsa el botón Compartir',
      desc: inSafari
        ? 'Es el cuadrado con la flecha hacia arriba, en la barra inferior de Safari.'
        : 'El cuadrado con la flecha hacia arriba. En este navegador puede estar dentro del menú “…”.',
    },
    {
      icon: SquarePlus,
      title: 'Elige “Añadir a pantalla de inicio”',
      desc: 'Está en la lista de opciones, un poco más abajo. Puede que tengas que deslizar.',
    },
    {
      icon: Check,
      title: 'Confirma con “Añadir”',
      desc: 'MiCoach queda como una app más, a pantalla completa y sin la barra del navegador.',
    },
  ];

  return (
    // El rol va en el panel, no en el fondo: index.css pinta cualquier
    // [role="dialog"] con --surface-1 e !important (override de Radix), y en
    // el fondo eso lo dejaba opaco en vez de dejar ver la página detrás.
    <div style={S.overlay}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="ios-install-title">
        <button style={S.close} onClick={onClose} aria-label="Cerrar">
          <X size={16} />
        </button>

        <div style={{ ...S.modalHeader, paddingRight: '1.5rem' }}>
          <div style={S.iconBadge}>
            <Smartphone size={18} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <h2 id="ios-install-title" style={S.modalTitle}>Instalar MiCoach en tu iPhone</h2>
            <p style={S.modalSub}>Tres pasos y lo tienes en la pantalla de inicio.</p>
          </div>
        </div>

        {!inSafari && (
          <p style={S.warning}>
            Si no encuentras la opción en este navegador, abre{' '}
            <strong>micoach.tech</strong> en <strong>Safari</strong>: ahí está siempre.
          </p>
        )}

        <ol style={S.steps}>
          {steps.map((step, i) => (
            <li key={i} style={S.step}>
              <span style={S.stepNum}>{i + 1}</span>
              <div style={S.stepBody}>
                <span style={S.stepTitle}>
                  <step.icon size={15} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  {step.title}
                </span>
                <span style={S.stepDesc}>{step.desc}</span>
              </div>
            </li>
          ))}
        </ol>

        <button style={{ ...S.btn, ...S.btnPrimary, width: '100%' }} onClick={onClose}>
          Entendido
        </button>
      </div>
    </div>
  );
}

// ─── Aviso flotante ──────────────────────────────────────────────────────────
export function InstallBanner() {
  const mode = useInstallMode();
  const [visible, setVisible] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (mode === 'none') { setVisible(false); return undefined; }
    if (installHintSnoozed()) return undefined;
    // Solo a quien ya ha usado la aplicación: a un visitante que acaba de
    // aterrizar en la landing no se le pide que se instale nada.
    if (!getLastPortal()) return undefined;
    // Con el banner de cookies en pantalla no se pisan dos avisos a la vez.
    if (!hasDecided()) return undefined;

    // Un respiro antes de aparecer, para no caer encima de la primera carga
    // de la pantalla.
    const t = setTimeout(() => setVisible(true), 12000);
    return () => clearTimeout(t);
  }, [mode]);

  const close = useCallback((forever = false) => {
    dismissInstallHint(forever);
    setVisible(false);
  }, []);

  const onInstall = useCallback(async () => {
    if (mode === 'ios') { setGuide(true); return; }
    const outcome = await promptInstall();
    // 'dismissed' = cerró el diálogo del navegador; se pospone, no se insiste.
    if (outcome === 'accepted') setVisible(false);
    else close(false);
  }, [mode, close]);

  if (!visible) return null;

  return (
    <>
      <div className="install-banner" role="region" aria-label="Instalar aplicación">
        <div style={S.bannerIcon}>
          <img src="/micoach-icon-192.png" alt="" style={S.bannerImg} />
        </div>

        <div style={S.bannerText}>
          <span style={S.bannerTitle}>Instala MiCoach</span>
          <span style={S.bannerDesc}>
            {mode === 'ios'
              ? 'Añádela a tu pantalla de inicio y ábrela como una app.'
              : 'Ábrela desde tu pantalla de inicio, sin barra del navegador.'}
          </span>
        </div>

        <div className="install-banner-actions">
          <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => close(false)}>
            Ahora no
          </button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={onInstall}>
            {mode === 'ios' ? 'Ver cómo' : 'Instalar'}
          </button>
        </div>

        <button style={S.bannerClose} onClick={() => close(true)} aria-label="No volver a mostrar">
          <X size={14} />
        </button>
      </div>

      {guide && <IOSGuide onClose={() => { setGuide(false); close(false); }} />}
    </>
  );
}

// ─── Entrada del menú ────────────────────────────────────────────────────────
// Desaparece sola cuando ya se entra como aplicación instalada (o cuando el
// navegador no puede instalar nada), así que no hace falta ni condicionarla
// desde fuera ni explicar por qué está ahí.
//
// onNavigate cierra el menú que la contiene. En iOS se llama al cerrar el
// tutorial, no al abrirlo: el modal se pinta dentro del menú y cerrarlo antes
// se lo llevaría por delante.
// wrapperClassName/wrapperStyle envuelven la entrada (separadores, márgenes)
// desde dentro: si se pusieran fuera quedaría un bloque vacío con su línea en
// los dispositivos donde no hay nada que instalar.
export function InstallMenuItem({ onNavigate, wrapperClassName, wrapperStyle }) {
  const mode = useInstallMode();
  const [guide, setGuide] = useState(false);

  if (mode === 'none') return null;

  const onClick = async () => {
    if (mode === 'ios') { setGuide(true); return; }
    onNavigate?.();
    await promptInstall();
  };

  const Icon = mode === 'ios' ? Share : Download;

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left"
        style={{
          background: 'var(--primary-soft)',
          border: '1px solid var(--primary-border)',
          color: 'var(--primary)',
        }}
        data-testid="install-app-menu-item"
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-semibold">
          {mode === 'ios' ? 'Añadir a la pantalla de inicio' : 'Instalar la app'}
        </span>
      </button>

      {guide && <IOSGuide onClose={() => { setGuide(false); onNavigate?.(); }} />}
    </div>
  );
}

// ─── Tarjeta de Ajustes ──────────────────────────────────────────────────────
// A diferencia del aviso, esta no se pospone: quien entra aquí lo hace a
// propósito. Cuando el navegador no soporta instalar (Firefox de escritorio,
// por ejemplo) se dice claramente en vez de dejar un botón que no hace nada.
export function InstallAppCard() {
  const mode = useInstallMode();
  const [guide, setGuide] = useState(false);
  const [done, setDone] = useState(false);

  const onInstall = async () => {
    if (mode === 'ios') { setGuide(true); return; }
    const outcome = await promptInstall();
    if (outcome === 'accepted') setDone(true);
  };

  const installed = mode === 'none' && (done || isInstalledLike());

  return (
    <>
      <div style={S.card}>
        <div style={S.modalHeader}>
          <div style={S.iconBadge}>
            <Smartphone size={18} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <h2 style={S.modalTitle}>MiCoach como aplicación</h2>
            <p style={S.modalSub}>
              Icono propio, pantalla completa y arranque directo, sin pasar por el navegador.
            </p>
          </div>
        </div>

        {installed && (
          <p style={S.okText}>
            <Check size={14} style={{ color: 'var(--primary)' }} />
            Ya la estás usando como aplicación instalada.
          </p>
        )}

        {!installed && mode === 'prompt' && (
          <button style={{ ...S.btn, ...S.btnPrimary, ...S.cardBtn }} onClick={onInstall}>
            <Download size={15} />
            Instalar aplicación
          </button>
        )}

        {!installed && mode === 'ios' && (
          <button style={{ ...S.btn, ...S.btnPrimary, ...S.cardBtn }} onClick={() => setGuide(true)}>
            <Share size={15} />
            Cómo instalarla en iPhone
          </button>
        )}

        {!installed && mode === 'none' && (
          <p style={S.helpText}>
            {isIOSDevice()
              ? 'Abre micoach.tech en Safari para poder añadirla a la pantalla de inicio.'
              : 'Este navegador no permite instalarla. Prueba con Chrome, Edge o Safari en iPhone.'}
          </p>
        )}
      </div>

      {guide && <IOSGuide onClose={() => setGuide(false)} />}
    </>
  );
}

// getInstallMode() devuelve 'none' tanto si ya está instalada como si el
// navegador no puede: para el texto de Ajustes hay que separarlo.
function isInstalledLike() {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

export default InstallBanner;

const S = {
  // Aviso flotante — la colocación vive en index.css (.install-banner), que
  // necesita media query para esquivar la barra de navegación móvil.
  bannerIcon: {
    width: 40, height: 40, borderRadius: 10, overflow: 'hidden',
    flexShrink: 0, background: 'var(--surface-2, #1A231C)',
  },
  bannerImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  bannerText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  bannerTitle: { fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-0, #f0f2f5)' },
  bannerDesc: {
    fontSize: '0.73rem', lineHeight: 1.4,
    color: 'var(--text-2, rgba(240,242,245,0.52))',
  },
  bannerClose: {
    position: 'absolute', top: 6, right: 6,
    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
    color: 'var(--text-2, rgba(240,242,245,0.52))', lineHeight: 0,
  },

  // Modal del tutorial
  close: {
    position: 'absolute', top: 10, right: 10,
    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
    color: 'var(--text-2, rgba(240,242,245,0.52))', lineHeight: 0,
  },
  overlay: {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1rem',
  },
  modal: {
    position: 'relative', width: '100%', maxWidth: 420,
    background: 'var(--surface-1, #161E18)',
    border: '1px solid var(--border-strong, rgba(255,255,255,0.10))',
    borderRadius: 14,
    boxShadow: 'var(--shadow-lg, 0 20px 50px rgba(0,0,0,0.5))',
    padding: '1.15rem',
    maxHeight: '85vh', overflowY: 'auto',
  },
  modalHeader: { display: 'flex', alignItems: 'flex-start', gap: '0.7rem', marginBottom: '0.9rem' },
  iconBadge: {
    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--primary-soft, rgba(16,185,129,0.12))',
    border: '1px solid var(--primary-border, rgba(16,185,129,0.25))',
  },
  modalTitle: {
    fontSize: '0.95rem', fontWeight: 700, margin: 0,
    color: 'var(--text-0, #f0f2f5)',
  },
  modalSub: {
    fontSize: '0.76rem', lineHeight: 1.5, margin: '0.15rem 0 0',
    color: 'var(--text-2, rgba(240,242,245,0.52))',
  },
  warning: {
    fontSize: '0.75rem', lineHeight: 1.5, margin: '0 0 0.9rem',
    padding: '0.6rem 0.7rem', borderRadius: 9,
    background: 'var(--primary-soft, rgba(16,185,129,0.12))',
    border: '1px solid var(--primary-border, rgba(16,185,129,0.25))',
    color: 'var(--text-1, rgba(240,242,245,0.78))',
  },
  steps: { listStyle: 'none', margin: '0 0 1rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.55rem' },
  step: {
    display: 'flex', gap: '0.65rem', alignItems: 'flex-start',
    padding: '0.65rem 0.7rem', borderRadius: 10,
    background: 'var(--surface-2, #1A231C)',
    border: '1px solid var(--border-subtle, rgba(255,255,255,0.055))',
  },
  stepNum: {
    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.68rem', fontWeight: 700,
    background: 'var(--primary, #10B981)', color: 'var(--primary-ink, #03150E)',
  },
  stepBody: { display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 },
  stepTitle: {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-0, #f0f2f5)',
  },
  stepDesc: {
    fontSize: '0.72rem', lineHeight: 1.5,
    color: 'var(--text-2, rgba(240,242,245,0.52))',
  },

  // Tarjeta de Ajustes
  card: {
    borderRadius: 16, padding: '1.5rem',
    background: 'var(--surface-0)',
    border: '1px solid var(--border-subtle)',
  },
  cardBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.1rem' },
  okText: {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    fontSize: '0.78rem', margin: 0, color: 'var(--text-1, rgba(240,242,245,0.78))',
  },
  helpText: {
    fontSize: '0.76rem', lineHeight: 1.5, margin: 0,
    color: 'var(--text-2, rgba(240,242,245,0.52))',
  },

  // Botones
  btn: {
    padding: '0.5rem 0.85rem', borderRadius: 9,
    fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    border: '1px solid transparent', whiteSpace: 'nowrap',
  },
  btnPrimary: { background: 'var(--primary, #10B981)', color: 'var(--primary-ink, #03150E)' },
  btnGhost: {
    background: 'transparent',
    color: 'var(--text-1, rgba(240,242,245,0.78))',
    borderColor: 'var(--border-strong, rgba(255,255,255,0.10))',
  },
};
